import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { TaskDetailsComponent } from '../../../pages/management/management.component';
import { CommonService } from '../../services/common.service';
import { ManagementService } from '../../../pages/management/management.service';
import { TeamService } from '../../../pages/team/team.service';
import { fullName } from '../../../pages/team/team-member.model';

/** The `/gp/:gpId/vehicle/:vehicleId/…` pair every task write needs. */
interface RouteContext {
  gpId: string;
  vehicleId: string;
}

type Priority = 'LOW' | 'MEDIUM' | 'HIGH';

/** One entry in the assignee picker, pre-rendered so the template stays dumb. */
interface AssigneeOption {
  id: string;
  label: string;
}

/**
 * Create a task from anywhere in the portal.
 *
 * This is a thin head on the existing task stack — it posts through
 * ManagementService.submitSuggestTask (same endpoint, same payload builder, same
 * `taskCreated` ping that refreshes an open board) rather than reimplementing
 * any of it. The full task drawer keeps the rest: assignees, checklists,
 * documents, ClickUp sync. This only covers the fields you'd want to capture
 * without losing your place, and then hands you a way into the real thing.
 */
@Component({
  selector: 'app-quick-task',
  templateUrl: './quick-task.component.html',
  styleUrls: ['./quick-task.component.scss'],
})
export class QuickTaskComponent implements OnInit, OnDestroy {
  /**
   * Whether the current user can see the task board. Passed in because
   * privileges live on the header's selected fund, not in a shared service —
   * creating a task you then can't open would be a dead end.
   */
  @Input() enabled = true;

  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;

  panelMode: 'form' | 'created' = 'form';
  isSubmitting = false;

  /** Kept after submit so the created state can name and open the task. */
  createdTaskId = '';
  createdTitle = '';

  readonly priorities: Priority[] = ['LOW', 'MEDIUM', 'HIGH'];

  /**
   * The task API requires exactly one assignee (`assigneeIds` must hold between
   * 1 and 1 UUIDs), so this is a required single select rather than the
   * drawer's multi-select — an unassigned quick task is not a thing the backend
   * will accept.
   */
  assignees: AssigneeOption[] = [];
  isLoadingTeam = false;
  teamUnavailable = false;

  form = this.fb.group({
    title: ['', [Validators.required]],
    assigneeId: ['', [Validators.required]],
    priority: ['MEDIUM' as Priority, [Validators.required]],
    dueAt: [''],
    description: [''],
  });

  routeContext: RouteContext | null = null;

  // The header sits outside the router outlet, so ActivatedRoute here is the
  // root and never carries :gpId/:vehicleId. Reading them off the URL is the
  // cheapest way to stay correct as the user moves between pages.
  private static readonly ROUTE_CONTEXT_PATTERN =
    /\/gp\/([^/?#]+)\/vehicle\/([^/?#]+)(?:[/?#]|$)/;

  private routerSub?: Subscription;
  private taskDetailsDialogRef?: MatDialogRef<TaskDetailsComponent>;

  /** Whoever the picker falls back to — the logged-in user where we know them. */
  private defaultAssigneeId = '';
  private assigneesLoaded = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private managementService: ManagementService,
    private teamService: TeamService,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.readRouteContext(this.router.url);
    this.routerSub = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(event => this.readRouteContext(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.taskDetailsDialogRef?.close();
  }

  /** Hidden wherever there's no fund in scope (login, error pages). */
  get canCreate(): boolean {
    return this.enabled && !!this.routeContext;
  }

  /**
   * Loads the firm team for the picker, on first open rather than on app start:
   * this component lives in the header of every page, and getFirmTeam() costs
   * two requests (/users/me + /team/members). TeamService caches the result for
   * the session and the task board asks for the same stream, so a second open —
   * or a later visit to the board — is free. Defaults to the logged-in user,
   * whom /users/me puts first. A failed load is retried on the next open.
   */
  ensureAssignees(): void {
    if (this.assigneesLoaded || this.isLoadingTeam) {
      return;
    }
    this.isLoadingTeam = true;
    this.teamService.getFirmTeam().subscribe({
      next: members => {
        this.assignees = (members ?? []).map(member => ({
          id: member.id,
          label: member.isCurrentUser
            ? `${fullName(member)} (You)`
            : fullName(member) || member.email,
        }));
        this.defaultAssigneeId =
          (members ?? []).find(member => member.isCurrentUser)?.id ??
          this.assignees[0]?.id ??
          '';
        this.teamUnavailable = !this.assignees.length;
        this.assigneesLoaded = !!this.assignees.length;
        this.form.patchValue({ assigneeId: this.defaultAssigneeId });
        this.isLoadingTeam = false;
      },
      // Without a team there is no valid assignee, so the form says so rather
      // than letting the user write a task the API will reject.
      error: () => {
        this.assignees = [];
        this.teamUnavailable = true;
        this.isLoadingTeam = false;
      },
    });
  }

  private readRouteContext(url: string): void {
    const match = QuickTaskComponent.ROUTE_CONTEXT_PATTERN.exec(url ?? '');
    this.routeContext = match
      ? {
          gpId: decodeURIComponent(match[1]),
          vehicleId: decodeURIComponent(match[2]),
        }
      : null;
  }

  selectPriority(priority: Priority): void {
    this.form.patchValue({ priority });
  }

  // Reset on close so the next open is a blank form rather than the previous
  // task's leftovers.
  onMenuClosed(): void {
    this.panelMode = 'form';
    this.isSubmitting = false;
    this.createdTaskId = '';
    this.createdTitle = '';
    this.form.reset({
      title: '',
      assigneeId: this.defaultAssigneeId,
      priority: 'MEDIUM',
      dueAt: '',
      description: '',
    });
  }

  submit(): void {
    const context = this.routeContext;
    if (this.form.invalid || this.isSubmitting || !context) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const title = (value.title ?? '').trim();
    // The API requires exactly one assignee UUID; never post without one.
    if (!title || !value.assigneeId) {
      return;
    }

    this.isSubmitting = true;
    this.managementService
      .submitSuggestTask(
        context.vehicleId,
        this.toFormData(title, value),
        context.gpId
      )
      .subscribe({
        next: response => {
          this.createdTaskId = this.extractTaskId(response);
          this.createdTitle = title;
          this.panelMode = 'created';
        },
        error: error => {
          this.isSubmitting = false;
          this.commonService.errorNotification(
            error?.error?.message ??
              error?.message ??
              'Unable to create the task for this fund.'
          );
        },
        complete: () => {
          this.isSubmitting = false;
        },
      });
  }

  /**
   * Go to the board and open the new task in the same centered details dialog
   * used from dashboard/onboarding, so the handoff stays consistent.
   */
  openCreatedTask(): void {
    const context = this.routeContext;
    const taskId = this.createdTaskId;
    const title = this.createdTitle;
    if (!context || !taskId) {
      return;
    }

    // Closing resets the panel, so everything needed is captured above first.
    this.menuTrigger?.closeMenu();

    this.router
      .navigate([`/gp/${context.gpId}/vehicle/${context.vehicleId}/management`])
      .then(navigated => {
        // Three outcomes, and only one of them means "don't open": `true` we
        // moved, `null` we were already on the board (the router's default
        // onSameUrlNavigation: 'ignore'), `false` a guard refused us.
        if (navigated === false) {
          return;
        }
        // setTaskDetails silently no-ops while a drawer is open, so close first
        // rather than relying on the drawer's backdrop having blocked the
        // header click that got us here.
        this.managementService.closeDrawer();
        this.managementService.setTaskDetails({
          taskId,
          title,
          vehicleId: context.vehicleId,
          gpId: context.gpId,
          isSuggest: false,
          isDialog: true,
        });
        this.taskDetailsDialogRef?.close();
        this.taskDetailsDialogRef = this.dialog.open(TaskDetailsComponent, {
          width: 'min(760px, calc(100vw - 32px))',
          maxWidth: 'calc(100vw - 32px)',
          height: 'min(760px, calc(100vh - 32px))',
          maxHeight: 'calc(100vh - 32px)',
          panelClass: 'task-details-dialog-panel',
          backdropClass: 'create-task-dialog-backdrop',
          ariaLabel: 'Task details',
          autoFocus: 'first-tabbable',
        });
        this.taskDetailsDialogRef.afterClosed().subscribe(() => {
          this.taskDetailsDialogRef = undefined;
          this.managementService.setTaskDetails(null);
        });
      });
  }

  private toFormData(
    title: string,
    value: {
      assigneeId: string | null;
      priority: Priority | null;
      dueAt: string | null;
      description: string | null;
    }
  ): FormData {
    const data = new FormData();
    data.append('source', 'native');
    data.append('title', title);
    // Serialised the way the service's formArrayValue expects to read it back,
    // and always exactly one id — the API rejects zero or more than one.
    data.append('assigneeIds', JSON.stringify([value.assigneeId]));
    data.append('priority', value.priority ?? 'MEDIUM');
    if (value.dueAt) {
      data.append('dueAt', value.dueAt);
    }
    if (value.description?.trim()) {
      data.append('description', value.description.trim());
    }
    // Match the full drawer's defaults; the rest of the payload (fund, folder,
    // assignees, checklist) is resolved or defaulted by the service.
    data.append('notifyInApp', 'true');
    data.append('notifyEmail', 'false');
    return data;
  }

  // The create response is an ACE envelope, and has been seen both as
  // `{ data: task }` and `{ data: { task } }` across this API — accept either
  // rather than lose the "open it" affordance to a shape mismatch.
  private extractTaskId(response: unknown): string {
    const data = (response as { data?: unknown })?.data ?? response;
    const task = (data as { task?: unknown })?.task ?? data;
    return (task as { id?: string })?.id ?? '';
  }
}
